import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate, formatDateTime } from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Package, ArrowsClockwise, Wrench, Warning, Truck, Plug,
  Buildings, Bank, MapPin, CheckCircle, UserCircle, CaretRight, FileText,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Single-asset 360° drawer — opens from an Asset Register row and
 * shows full lifecycle: details · movements · maintenance/calibrations ·
 * incidents · mobilisations · rentals · accessories · totals (cost-to-date).
 *
 * Backed by GET /api/assets/{asset_id}/lifecycle.
 */
export default function AssetLifecycleDrawer({ asset, trigger, open: openProp, onOpenChange: setOpenProp }) {
  const [openLocal, setOpenLocal] = useState(false);
  const isControlled = typeof openProp === "boolean";
  const open = isControlled ? openProp : openLocal;
  const setOpen = isControlled ? (setOpenProp || (() => {})) : setOpenLocal;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !asset?.asset_id) return;
    setLoading(true);
    api.get(`/assets/${encodeURIComponent(asset.asset_id)}/lifecycle`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, asset?.asset_id]);

  const STATUS_TONE = {
    in_store:    "bg-slate-100 text-slate-700",
    deployed:    "bg-emerald-100 text-emerald-800",
    rented:      "bg-blue-100 text-blue-800",
    maintenance: "bg-amber-100 text-amber-800",
    calibration: "bg-amber-100 text-amber-800",
    retired:     "bg-zinc-200 text-zinc-700",
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <SheetTrigger asChild>
          {trigger || (
            <Button size="sm" variant="outline" data-testid={`asset-360-btn-${asset?.asset_id}`}>
              <Package size={12} className="mr-1.5" /> 360° View
            </Button>
          )}
        </SheetTrigger>
      )}
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <Package size={18} weight="fill" style={{ color: "#1E5CBA" }} />
            {asset?.asset_id} · {asset?.name || asset?.description || ""}
          </SheetTitle>
          <SheetDescription>
            Full asset lifecycle — every movement, maintenance event, incident, mobilisation, rental and accessory linked to this asset.
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="mt-6 font-mono-alt text-xs uppercase tracking-widest text-slate-500" data-testid="asset-360-loading">Loading lifecycle…</div>
        ) : !data ? null : (
          <div className="mt-5 space-y-5" data-testid={`asset-360-drawer-${asset?.asset_id}`}>
            {/* Top KPI strip */}
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <KpiTile label="Status" value={data.current.status || "—"} tone={STATUS_TONE[data.current.status] || "bg-slate-100 text-slate-700"} testid="kpi-status" />
              <KpiTile label="Cost to date" value={formatCurrency(data.totals.cost_to_date)} valueTone="text-blue-700" testid="kpi-cost" />
              <KpiTile label="Maintenance" value={`${data.totals.maintenance} ev · $${Math.round(data.totals.maintenance_cost_total).toLocaleString()}`} testid="kpi-maint" />
              <KpiTile label="Calibration due" value={data.current.calibration_due ? formatDate(data.current.calibration_due) : "—"} valueTone={isOverdue(data.current.calibration_due) ? "text-red-700" : isSoon(data.current.calibration_due) ? "text-amber-700" : "text-slate-700"} testid="kpi-cal" />
            </div>

            {/* Details */}
            <Section icon={Buildings} title="Asset Details" testid="sec-details">
              <DetailRow label="Asset ID" value={data.asset.asset_id} />
              <DetailRow label="Category" value={`${data.asset.category} · ${data.asset.sub_category || "—"}`} />
              <DetailRow label="Storage zone" value={data.asset.storage_zone || "—"} />
              <DetailRow label="Model · Serial" value={`${data.asset.model_no || "—"} · ${data.asset.serial_number || "—"}`} />
              <DetailRow label="Vendor" value={data.asset.vendor || "—"} />
              <DetailRow label="Purchase" value={`${data.asset.purchase_date || "—"} · ${formatCurrency(data.asset.purchase_cost || data.asset.purchase_value_usd || 0)}`} />
              <DetailRow label="Warranty expiry" value={data.asset.warranty_expiry || "—"} />
              <DetailRow label="Useful life" value={data.asset.useful_life_years ? `${data.asset.useful_life_years} yrs` : "—"} />
              <DetailRow label="Custodian" value={data.current.custodian || "Unassigned"} icon={UserCircle} />
              <DetailRow label="Current location" value={data.current.location || "—"} icon={MapPin} />
              {data.current.project_name && <DetailRow label="Deployed on" value={data.current.project_name} />}
            </Section>

            {/* Mobilisations */}
            {data.mobilisations.length > 0 && (
              <Section icon={Truck} title={`Mobilisations · ${data.totals.mobilisations}`} testid="sec-mobs">
                {data.mobilisations.map((m) => (
                  <TimelineRow key={m.id}
                    when={formatDate(m.deployment_date)}
                    title={m.destination}
                    sub={`PM: ${m.project_manager || "—"} · ${m.asset_count || 1} assets · expected return ${formatDate(m.expected_return) || "—"}`}
                    status={m.status}
                    statusTone={mobTone(m.status)}
                    testid={`mob-${m.id}`}
                  />
                ))}
              </Section>
            )}

            {/* Movements */}
            {data.movements.length > 0 && (
              <Section icon={ArrowsClockwise} title={`Movements · ${data.totals.movements}`} testid="sec-moves">
                {data.movements.map((m) => (
                  <TimelineRow key={m.id}
                    when={formatDateTime(m.created_at)}
                    title={`${m.movement_type.replace("_", " ")} → ${m.destination || "—"}`}
                    sub={`by ${m.operator || "—"} · ${m.purpose || ""}`}
                    status={m.movement_type}
                    statusTone={movementTone(m.movement_type)}
                    testid={`mov-${m.id}`}
                  />
                ))}
              </Section>
            )}

            {/* Maintenance / Calibrations */}
            {data.maintenance.length > 0 && (
              <Section icon={Wrench} title={`Maintenance · ${data.totals.maintenance} (${data.totals.calibrations} calibration${data.totals.calibrations === 1 ? "" : "s"})`} testid="sec-maint">
                {data.maintenance.map((m) => (
                  <TimelineRow key={m.id}
                    when={formatDate(m.date_performed)}
                    title={m.description || m.maintenance_type}
                    sub={`by ${m.performed_by || "—"} · ${formatCurrency(m.cost || 0)} · next: ${formatDate(m.next_due_date) || "—"}${m.certificate_ref ? ` · cert ${m.certificate_ref}` : ""}`}
                    status={m.maintenance_type}
                    statusTone={m.maintenance_type === "calibration" ? "bg-blue-100 text-blue-700" : m.maintenance_type === "corrective" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}
                    testid={`maint-${m.id}`}
                  />
                ))}
              </Section>
            )}

            {/* Rentals */}
            {data.rentals.length > 0 && (
              <Section icon={Bank} title={`Rentals · ${data.totals.rentals}`} testid="sec-rentals">
                {data.rentals.map((r) => (
                  <TimelineRow key={r.id}
                    when={`${formatDate(r.rental_start)} → ${formatDate(r.rental_end)}`}
                    title={r.client_name}
                    sub={`${formatCurrency(r.daily_rate_usd)}/day · ${formatCurrency(r.total_value_usd)} total`}
                    status={r.status}
                    statusTone={r.status === "active" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"}
                    testid={`rent-${r.id}`}
                  />
                ))}
              </Section>
            )}

            {/* Incidents */}
            {data.incidents.length > 0 && (
              <Section icon={Warning} title={`Incidents · ${data.totals.incidents}`} testid="sec-incidents">
                {data.incidents.map((i) => (
                  <TimelineRow key={i.id}
                    when={formatDate(i.date_discovered)}
                    title={`${i.nature} · ${i.location || "—"}`}
                    sub={`${i.details || ""}${i.police_reference ? ` · Police ref: ${i.police_reference}` : ""}`}
                    status={i.status}
                    statusTone={incidentTone(i.status)}
                    testid={`inc-${i.id}`}
                  />
                ))}
              </Section>
            )}

            {/* Accessories */}
            {data.accessories.length > 0 && (
              <Section icon={Plug} title={`Accessories · ${data.totals.accessories}`} testid="sec-accessories">
                {data.accessories.map((a) => (
                  <TimelineRow key={a.id}
                    when={a.accessory_id}
                    title={a.description}
                    sub={`Qty: ${a.quantity || 1} · ${a.storage_zone || "—"}`}
                    status={a.status}
                    statusTone={a.status === "lost" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}
                    testid={`acc-${a.id}`}
                  />
                ))}
              </Section>
            )}

            {/* Asset Requests (Phase 2) */}
            {(data.asset_requests || []).length > 0 && (
              <Section icon={CheckCircle} title={`Requests · ${data.totals.asset_requests || data.asset_requests.length}`} testid="sec-asset-requests">
                {data.asset_requests.map((r) => (
                  <TimelineRow key={r.id}
                    when={formatDateTime(r.created_at)}
                    title={`${(r.request_type || "").replace("_", " ")} · ${r.requester_name || r.requester_email}`}
                    sub={`${r.purpose || r.incident_details || "—"}${r.destination ? ` · ${r.destination}` : ""}${r.approver_name ? ` · decided by ${r.approver_name}` : ""}`}
                    status={r.status}
                    statusTone={
                      r.status === "approved" ? "bg-emerald-100 text-emerald-800"
                      : r.status === "rejected" ? "bg-rose-100 text-rose-800"
                      : r.status === "pending" ? "bg-amber-100 text-amber-800"
                      : "bg-slate-100 text-slate-700"
                    }
                    testid={`req-${r.id}`}
                  />
                ))}
              </Section>
            )}

            {/* Audit Trail (Phase 3) */}
            {(data.audit || []).length > 0 && (
              <Section icon={FileText} title={`Audit Trail · ${data.totals.audit || data.audit.length}`} testid="sec-asset-audit">
                <div className="space-y-2">
                  {data.audit.slice(0, 30).map((a, idx) => (
                    <div key={a.id || idx} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs" data-testid={`audit-${a.id || idx}`}>
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="font-mono-alt text-[10px] uppercase tracking-wider text-slate-500">{formatDateTime(a.created_at)}</span>
                        <span className="font-mono-alt text-[10px] uppercase tracking-wider text-blue-700">{a.action}</span>
                        <span className="text-slate-700">{a.user_email || "system"}</span>
                      </div>
                      {a.diff && a.diff.length > 0 && (
                        <div className="mt-1.5 space-y-0.5">
                          {a.diff.slice(0, 6).map((d, i) => (
                            <div key={i} className="text-[11px] text-slate-600">
                              <span className="font-mono-alt text-[10px] uppercase tracking-wider text-slate-500">{d.field}: </span>
                              <span className="font-mono text-rose-700 line-through">{String(d.from ?? "—")}</span>
                              <span className="mx-1 text-slate-400">→</span>
                              <span className="font-mono text-emerald-700">{String(d.to ?? "—")}</span>
                            </div>
                          ))}
                          {a.diff.length > 6 && <div className="text-[10px] text-slate-400">+{a.diff.length - 6} more field change(s)…</div>}
                        </div>
                      )}
                      {a.comments && <div className="mt-1 text-[11px] italic text-slate-500">{`"${a.comments}"`}</div>}
                    </div>
                  ))}
                  {data.audit.length > 30 && <div className="text-[10px] text-slate-400">Showing first 30 of {data.audit.length} entries.</div>}
                </div>
              </Section>
            )}

            {/* Empty lifecycle */}
            {data.totals.movements === 0 && data.totals.maintenance === 0 && data.totals.incidents === 0 && data.totals.rentals === 0 && data.totals.accessories === 0 && data.totals.mobilisations === 0 && (data.asset_requests || []).length === 0 && (
              <div className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500" data-testid="asset-360-empty-lifecycle">
                <CheckCircle size={20} weight="fill" className="text-emerald-500 mx-auto mb-1" />
                No lifecycle activity recorded yet for this asset. Once it gets checked out, deployed, calibrated or has an incident logged, the history will appear here.
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function isOverdue(d) {
  return d && new Date(d) < new Date();
}
function isSoon(d) {
  if (!d) return false;
  const t = new Date(d).getTime();
  const now = Date.now();
  return t >= now && t - now < 30 * 86400000;
}

function movementTone(type) {
  return ({
    check_out: "bg-amber-100 text-amber-800",
    check_in: "bg-emerald-100 text-emerald-800",
    transfer: "bg-blue-100 text-blue-800",
    rental_out: "bg-blue-100 text-blue-800",
    rental_in: "bg-emerald-100 text-emerald-800",
  })[type] || "bg-slate-100 text-slate-700";
}
function mobTone(status) {
  return ({
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-blue-100 text-blue-800",
    deployed: "bg-emerald-100 text-emerald-800",
    returned: "bg-slate-100 text-slate-700",
    rejected: "bg-red-100 text-red-700",
  })[status] || "bg-slate-100 text-slate-700";
}
function incidentTone(status) {
  return ({
    open: "bg-red-100 text-red-700",
    investigating: "bg-amber-100 text-amber-800",
    resolved: "bg-emerald-100 text-emerald-800",
    closed: "bg-slate-100 text-slate-700",
  })[status] || "bg-slate-100 text-slate-700";
}

function KpiTile({ label, value, tone, valueTone, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`mt-0.5 ${tone ? `inline-flex rounded px-2 py-0.5 text-xs font-mono-alt uppercase tracking-wider ${tone}` : `font-mono-alt tabular-nums font-semibold text-sm ${valueTone || "text-slate-900"}`}`}>
        {value}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="rounded-lg border border-slate-200" data-testid={testid}>
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5">
        {Icon && <Icon size={14} weight="fill" style={{ color: "#1E5CBA" }} />}
        <h4 className="font-display text-sm font-semibold text-slate-900">{title}</h4>
      </div>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  );
}

function DetailRow({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-1.5 text-sm">
      <span className="overline text-slate-500 flex items-center gap-1.5">{Icon && <Icon size={11} />} {label}</span>
      <span className="text-slate-800 text-right break-words">{value}</span>
    </div>
  );
}

function TimelineRow({ when, title, sub, status, statusTone, testid }) {
  return (
    <div className="px-4 py-2.5 flex items-start gap-3" data-testid={testid}>
      <CaretRight size={11} className="mt-1 text-slate-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-slate-900 break-words flex-1">{title}</span>
          {status && (
            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider whitespace-nowrap ${statusTone || "bg-slate-100 text-slate-700"}`}>
              {status}
            </span>
          )}
        </div>
        {sub && <div className="mt-0.5 text-xs text-slate-600 break-words">{sub}</div>}
        {when && <div className="mt-0.5 font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">{when}</div>}
      </div>
    </div>
  );
}
